import { useState } from "react";
import { Link } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";

interface Faq {
  q: string;
  a: string;
}

const groups: { title: string; desc: string; items: Faq[] }[] = [
  {
    title: "关于热度与榜单",
    desc: "了解热议焦点的热度是怎么来的",
    items: [
      {
        q: "话题的热度值是怎么计算的？",
        a: "我们综合公开讨论中的声量、讨论增量与传播速度，做归一化处理后得到一个相对热度。它主要用来判断话题的「热度档位」和横向比较，而非绝对精确的传播量，数值仅在同一语境下具备参考意义。",
      },
      {
        q: "为什么有些很火的话题不在榜上？",
        a: "榜单优先呈现讨论还在快速上升、处于「发酵期」的话题。一些已经进入长尾、增长趋缓的老话题，可能被更新的热点挤下去，但这不代表它不重要。",
      },
      {
        q: "「爆」「热」「发酵中」是什么意思？",
        a: "这是我们用来描述话题热度档位的三个标签：爆代表讨论进入峰值并广泛破圈，热代表讨论维持在高位，「发酵中」代表讨论正在升温、值得提前关注。",
      },
    ],
  },
  {
    title: "关于观点归纳",
    desc: "了解网友观点如何被整理",
    items: [
      {
        q: "网友观点占比是怎么来的？",
        a: "我们从公开可读的评论中抽样归纳，先划分主要立场，再按相近观点聚类。占比反映的是抽样范围内的相对分布，帮助快速感知舆论大方向，并非精确统计。",
      },
      {
        q: "归纳的观点会不会遗漏少数声音？",
        a: "会。为了可读性，我们只呈现占比靠前、最具代表性的立场，极端或零散的少数意见可能不会逐一列出，但不代表我们没有关注到。",
      },
      {
        q: "天涯吃瓜的内容代表官方立场吗？",
        a: "不代表。我们梳理的是「讨论」本身，展示的是网友意见的结构，并不对话题涉及的事实或对错作出官方判断。请结合多方信息独立判断。",
      },
    ],
  },
  {
    title: "使用与互动",
    desc: "关于阅读体验与进一步了解",
    items: [
      {
        q: "我可以投稿或提供话题线索吗？",
        a: "非常欢迎。如果你观察到正在升温但还未出圈的讨论苗头，可以通过关于我们页面的邮箱发给我们，编辑部会评估是否纳入追踪。",
      },
      {
        q: "内容多久更新一次？",
        a: "我们会全天候关注讨论动态，热门议题通常会在数小时内完成时间线与观点归纳，并滚动更新首页的热度榜单。",
      },
      {
        q: "我可以在转载时使用站内内容吗？",
        a: "非商业性质的转载请在显著位置注明出处并附上原文链接；商业合作请先与我们联系获得授权。",
      },
    ],
  },
];

const faqSchema = groups.flatMap((g) => g.items);

export default function Faq() {
  usePageMeta(
    "常见问题 - 天涯吃瓜热议焦点与舆论风向观察",
    "关于天涯吃瓜的热度计算、观点归纳、使用与互动的常见问题说明，帮助你更好地追踪热议焦点、看懂舆论风向与话题发酵。",
    "天涯吃瓜常见问题,天涯吃瓜热议焦点,天涯吃瓜热度计算,天涯吃瓜网友评论,天涯吃瓜舆论风向"
  );
  const [open, setOpen] = useState<string | null>("0-0");

  const toggle = (key: string) => setOpen((cur) => (cur === key ? null : key));

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqSchema.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  return (
    <div>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <section className="border-b border-background-200 bg-gradient-to-br from-background-100 via-background-50 to-accent-100/50">
        <div className="mx-auto max-w-4xl px-4 py-12 text-center md:py-16">
          <span className="text-xs font-semibold tracking-wide text-primary-600">HELP CENTER</span>
          <h1 className="mt-3 font-heading text-3xl font-black text-foreground-950 md:text-4xl">常见问题</h1>
          <p className="mx-auto mt-3 max-w-2xl text-sm leading-7 text-foreground-600">
            关于天涯吃瓜的热度、观点归纳与使用方式，多数疑问都能在这里找到答案。
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-10 md:px-6">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_300px]">
          <div className="space-y-8">
            {groups.map((g, gi) => (
              <section key={g.title} aria-label={g.title}>
                <div className="flex items-center gap-3">
                  <h2 className="font-heading text-lg font-bold text-foreground-900 md:text-xl">{g.title}</h2>
                  <span className="text-xs text-foreground-400">{g.desc}</span>
                </div>
                <div className="mt-4 divide-y divide-background-100 overflow-hidden rounded-2xl border border-background-200 bg-white">
                  {g.items.map((f, fi) => {
                    const key = `${gi}-${fi}`;
                    const isOpen = open === key;
                    return (
                      <div key={key}>
                        <button
                          type="button"
                          onClick={() => toggle(key)}
                          aria-expanded={isOpen}
                          className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-background-50"
                        >
                          <span className="text-sm font-medium text-foreground-800 md:text-base">
                            {f.q}
                          </span>
                          <i
                            className={`shrink-0 text-xl text-primary-500 transition-transform ${isOpen ? "rotate-45" : ""}`}
                            aria-hidden="true"
                          ></i>
                        </button>
                        {isOpen && (
                          <p className="px-5 pb-5 text-sm leading-7 text-foreground-500">{f.a}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>

          <aside className="space-y-6">
            <div className="rounded-2xl border border-background-200 bg-white p-5">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-900">
                <i className="ri-service-line text-primary-500" aria-hidden="true"></i>
                还有疑问？
              </h3>
              <p className="mt-2 text-sm leading-6 text-foreground-500">
                没找到想要的答案？可以先去逛逛热议焦点，或到关于我们页面了解更完整的说明。
              </p>
              <div className="mt-4 flex flex-col gap-2">
                <Link
                  to="/topics"
                  className="inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-4 text-sm font-medium text-white hover:bg-primary-600"
                >
                  浏览热议焦点
                </Link>
                <Link
                  to="/about"
                  className="inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-full border border-background-200 px-4 text-sm font-medium text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                >
                  关于我们
                </Link>
              </div>
            </div>

            <div className="rounded-2xl border border-background-200 bg-secondary-100 p-5">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-secondary-900">
                <i className="ri-quill-pen-line text-secondary-700" aria-hidden="true"></i>
                快速上手
              </h3>
              <p className="mt-2 text-sm leading-6 text-secondary-800">
                想立刻体验？从首页的热度榜开始，挑一个正在发酵的话题，点进去看它的时间线与观点占比即可。
              </p>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}